import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  matchPath,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import isEqual from 'lodash/isEqual'
import Hotjar from '@hotjar/browser'
import { withStyles } from '~/themes/useStyles'
import { useSyncedSignOut } from '~/modules/auth/utils/syncedSignOut'
import { isActivePointReward, isValidUser } from '~/modules/auth/conditions'
import { useAuth } from '~/modules/auth/redux/hook'
import { useUser } from '~/redux/hooks/user'
import Guard from '~/modules/auth/components/Guard'
// import Topbar from '~/components/common/Layout/Topbar'
import style from './style'
import StoreGuard from '~/components/Guard/StoreGuard'
import RoleGuard from '~/components/Guard/RoleGuard'
import BackToTopButton from '~/components/common/BackToTop'
import {
  ADMIN_ROUTES,
  PROMOTION_ROUTES,
  POINT_AND_REWARDS_ROUTERS,
} from '~/constants/router'
import { urlWithStoreID } from '~/utils/common'
// import DataLoading from '~/components/common/DataLoading'
import { goToTop } from '~/utils/scrollUp'
import useGATracking from '~/utils/hooks/useGATracking'
import {
  PENDO_API_KEY,
  HOTJAR_SITE_ID,
  REACT_APP_NOTIFICATION_URL,
  REACT_APP_COM_HUB_API_URL,
} from '~/constants/environment'
import growthbook from '~/configs/growthBook'
import { useIsSyncedStore, useStoreType } from '~/redux/hooks/store'
import { STORE_TYPES } from '~/constants/store'
import { useFetchingIncompletedOrders } from '~/redux/hooks/cantecFulfillment'
import logger from '~/utils/logger'
import UnifiedNavbar from '~/components/common/Layout/UnifiedNavbar'
import { useUserSession } from '~/utils/hooks/useUserSession'
import authApi from '~/modules/auth/services'
import { useActiveStore } from '~/redux/hooks/store'

import { useWHLocationOptions } from '~/utils/hooks/useOrgWarehouses'
import {
  NotificationProvider,
  NotificationType,
  AnnouncementProvider,
  Announcement,
} from 'bs-unified-ui'
import fbAuth from '~/modules/firebase'
import { TIMEZONE_SETTING } from '~/constants/settings'
import { USER_DEVICE_ZONE } from '~/constants/timezone'
import { VANCOUVER_TZ } from '~/constants/share'
import { useNotificationSound } from '~/utils/hooks/useNotificationSound'
import { useGetMe } from '~/pages/Account/Notification/hooks/useGetMe'
import useNotification from '~/pages/Account/Notification/hooks/useNotification'
import { useCanfleetNotification } from '~/pages/Account/Notification/hooks/useCanfleetNotification'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { FLAGS } from '~/constants/flags'
import { useClaims } from '~/utils/hooks/useClaims'
import { useCheckCanfleetUser } from '~/pages/Settings/hooks/useCheckCanfleetUser'

export const USER_ROLES = {
  AGENT: 'agent',
  CHATSO_ADMIN: 'coach',
  ADMIN: 'admin',
  CUSTOM: 'custom_role',
  MARKETING_MANAGER: 'marketing_manager',
  MARKETING_AGENT: 'marketing_agent',
}

const AdminLayout = ({ classes, children }) => {
  const isCF1997Enabled = useFeatureIsOn(FLAGS.CF_1997)
  const isNotiV2 = useFeatureIsOn(FLAGS.CS_9035)

  const location = useLocation()
  useSyncedSignOut()
  useGATracking()
  const { storeId } = useParams()
  const navigate = useNavigate()
  const { actions, authState } = useAuth()
  const {
    actions: userActions,
    orgId,
    user: { user_settings },
  } = useUser()
  const pendoRef = useRef('')
  const storeType = useStoreType(storeId)
  const [disable, setDisable] = useState(false)
  const isSyncedStore = useIsSyncedStore()
  const activeStore = useActiveStore()
  const {
    user: { user_settings: userSettings },
  } = useUser()

  const { claims } = useClaims()
  const { data: checkCanfleetUser } = useCheckCanfleetUser(
    storeId,
    claims?.email,
    !!claims?.is_canfleet,
  )
  const isCanfleet = !!checkCanfleetUser?.userExistsInCf

  const { data: userMe, refetch: refetchUser } = useGetMe()

  const [sessionData, setSessionData] = useUserSession()
  const { data: locationOptions } = useWHLocationOptions(storeId, {
    defaultAllValue: -1,
  })

  const { data: cfNotificationSettings, refetch: refetchCanfleetNotification } =
    useCanfleetNotification(checkCanfleetUser?.cfOrgId, isCanfleet)

  const { data: notificationData, actions: notificationSettingActions } =
    useNotification(storeId)

  const currentWorkSpaceUserRole = useMemo(() => {
    return userMe?.workspaces?.find(
      (ws: any) => ws?.workspaceId?.id === activeStore?.csWsId,
    )
  }, [userMe, storeId])

  const timezoneNameUserSetting = useMemo(() => {
    if (userSettings?.tz_setting === TIMEZONE_SETTING.AUTO_DETECT) {
      return USER_DEVICE_ZONE
    }
    if (userSettings?.tz_setting === TIMEZONE_SETTING.MANUALLY) {
      return userSettings?.tz_timezone
    }
    return VANCOUVER_TZ
  }, [userSettings?.tz_setting, userSettings?.tz_timezone])

  const soundConfig = useMemo(() => {
    if (isNotiV2)
      return {
        ...userMe?.config?.chatNotificationSound?.soundConfig,
        delivery: {
          mute: cfNotificationSettings?.mute,
          url: cfNotificationSettings?.url,
          repeatingInterval: cfNotificationSettings?.repeatingInterval,
        },
      }

    return userMe?.config?.chatNotificationSound?.soundConfig
  }, [
    userMe?.config?.chatNotificationSound?.soundConfig,
    cfNotificationSettings?.mute,
    cfNotificationSettings?.url,
    cfNotificationSettings?.repeatingInterval,
    isNotiV2,
  ])

  const canAccessLiveChat = useMemo(() => {
    if (currentWorkSpaceUserRole?.role === USER_ROLES.CUSTOM)
      return currentWorkSpaceUserRole?.customRoles?.chatso_live_chat
    return [
      USER_ROLES.ADMIN,
      USER_ROLES.CHATSO_ADMIN,
      USER_ROLES.AGENT,
    ].includes(currentWorkSpaceUserRole?.role)
  }, [currentWorkSpaceUserRole])

  const canAccessOrder = useMemo(() => {
    if (currentWorkSpaceUserRole?.role === USER_ROLES.CUSTOM)
      return currentWorkSpaceUserRole?.customRoles?.breadstack_order
    return [
      USER_ROLES.ADMIN,
      USER_ROLES.CHATSO_ADMIN,
      USER_ROLES.AGENT,
      USER_ROLES.MARKETING_MANAGER,
      USER_ROLES.MARKETING_AGENT,
    ].includes(currentWorkSpaceUserRole?.role)
  }, [currentWorkSpaceUserRole])

  const triggerEvents = useMemo(
    () => ({
      new_pending_chat:
        canAccessLiveChat &&
        userMe?.config?.chatNotificationSound?.pendingChat?.enabled,
      new_unread_message:
        canAccessLiveChat &&
        userMe?.config?.chatNotificationSound?.newMessage?.enabled,
      customer_left:
        canAccessLiveChat &&
        userMe?.config?.chatNotificationSound?.customerLeft?.enabled,
      missed_chat:
        canAccessLiveChat &&
        userMe?.config?.chatNotificationSound?.missedChat?.enabled,
      new_incoming_order: canAccessOrder && notificationData?.new_order,
      cancelled_order: canAccessOrder && notificationData?.order_cancelled,
      task_created: cfNotificationSettings?.task_update?.enable,
      task_completed: cfNotificationSettings?.task_update?.enable,
      task_failed: cfNotificationSettings?.task_update?.enable,
      failed_to_pick_up: cfNotificationSettings?.task_update?.enable,
      success_to_pick_up: cfNotificationSettings?.task_update?.enable,
      failed_to_return: cfNotificationSettings?.task_update?.enable,
      success_to_return: cfNotificationSettings?.task_update?.enable,
      import_task: cfNotificationSettings?.import_csv?.enable,
      new_driver: cfNotificationSettings?.new_driver?.enable,
    }),
    [
      userMe?.config?.chatNotificationSound?.pendingChat?.enabled,
      userMe?.config?.chatNotificationSound?.newMessage?.enabled,
      userMe?.config?.chatNotificationSound?.customerLeft?.enabled,
      userMe?.config?.chatNotificationSound?.missedChat?.enabled,
      notificationData?.new_order,
      notificationData?.order_cancelled,
      canAccessLiveChat,
      canAccessOrder,
      cfNotificationSettings,
    ],
  )

  const warehousesConfig = useMemo(() => {
    const listConfig = notificationData?.warehouses?.reduce((acc, w) => {
      if ('all' in w) {
        if (w?.all) return [...acc, 'all']
        return acc
      }
      return [...acc, w?.warehouse_id]
    }, [])
    return listConfig
  }, [notificationData?.warehouses])

  // Initialize notification sound and get play functions
  const { playNewTaskSound, playNewOrderSound } = useNotificationSound()

  useEffect(() => {
    const isContainLocation =
      Array.isArray(locationOptions) &&
      locationOptions.some(
        (item) => item.value === user_settings?.default_location,
      )
    if (
      user_settings?.default_location &&
      isContainLocation &&
      !sessionData.location_id
    ) {
      setSessionData({
        ...sessionData,
        location_id: user_settings?.default_location,
      })
    }
  }, [user_settings?.default_location, locationOptions, sessionData])

  const hotjarRef = useRef(null)
  const firebaseUserId = authApi.getUserFirebaseId()

  useEffect(() => {
    const params = {
      id: authState?.data?.userId,
      email: authState?.data?.userEmail,
      full_name: authState?.data?.fullName,
      org_id: orgId,
      org_name: authState?.data?.orgs ? authState?.data?.orgs[orgId]?.name : '',
      store_id: storeId,
      role: authState?.data?.stores
        ? authState?.data?.stores[storeId]?.roleId
        : '',
    }

    if (firebaseUserId && HOTJAR_SITE_ID) {
      if (isEqual(hotjarRef.current, params)) return
      hotjarRef.current = params
      Hotjar.identify(firebaseUserId, params)
    }
  }, [
    firebaseUserId,
    authState?.data?.userEmail,
    authState?.data?.fullName,
    authState?.data?.userId,
    authState?.data?.orgs,
    authState?.data?.stores,
    orgId,
    storeId,
  ])

  useEffect(() => {
    const params = {
      visitor: {
        id: authState?.data?.userId,
        email: authState?.data?.userEmail,
        full_name: authState?.data?.fullName,
        role: authState?.data?.stores
          ? authState?.data?.stores[storeId]?.roleId
          : '',
      },
      account: {
        id: orgId,
        name: authState?.data?.orgs ? authState?.data?.orgs[orgId]?.name : '',
        is_paying: false,
      },
    }
    growthbook.setAttributes({
      id: authState?.data?.userId,
      email: authState?.data?.userEmail,
      role: authState?.data?.stores
        ? authState?.data?.stores[storeId]?.roleId
        : '',
      orgId,
      storeId,
      storeType: activeStore?.storeType,
      companyId: activeStore?.companyId,
      firebaseUserId: firebaseUserId || authState?.data?.userId,
    })
    if (isEqual(pendoRef.current, params)) return
    if (PENDO_API_KEY) {
      //@ts-ignore
      pendoRef.current = params
      //@ts-ignore
      window.pendo?.initialize(params)
    }
  }, [
    authState,
    orgId,
    storeId,
    activeStore?.storeType,
    activeStore?.companyId,
    firebaseUserId,
  ])

  useEffect(() => {
    const { pathname } = location
    if (new RegExp(/media/g).test(pathname)) {
      goToTop('content-folder')
      goToTop('mediaListFile')
      setDisable(true)
    } else if (new RegExp(/(promotion|custom-report)/g).test(pathname)) {
      setDisable(true)
    } else {
      setDisable(false)
    }
  }, [location])

  useEffect(() => {
    logger.info('Check auth state at admin layout')
    actions.checkAuthState()
  }, [])

  useEffect(() => {
    if (
      orgId &&
      storeId &&
      storeType !== STORE_TYPES.SHOPIFY &&
      isSyncedStore
    ) {
      actions.getPlugins({
        onSuccess: (plugins) => {
          const isInPoint = !!matchPath(
            {
              path: `${ADMIN_ROUTES.STORE}${POINT_AND_REWARDS_ROUTERS.POINT}/:id?`,
            },
            location.pathname,
          )
          if (isInPoint && !isActivePointReward()({ plugins })) {
            navigate(urlWithStoreID(PROMOTION_ROUTES.PROMOTION_RAW, storeId), {
              replace: true,
            })
          }
        },
        onError: () => {},
      })
    }
  }, [orgId, storeId, storeType, isSyncedStore])

  const handleNotification = useCallback(
    (notification) => {
      if (notification?.event === NotificationType.SYNC_SETTINGS) {
        // Refetch notification settings
        notificationSettingActions.getNotificationSettings([storeId])

        // Refetch user data
        refetchUser?.()

        // Refetch canfleet data
        if (isCanfleet && checkCanfleetUser?.cfOrgId) {
          refetchCanfleetNotification?.()
        }
      }
    },
    [notificationSettingActions, refetchUser, storeId, isCanfleet],
  )

  useEffect(() => {
    if (orgId) {
      userActions.getOrg({ organizations_list: [{ organization_id: orgId }] })
    }
    if (storeId) {
      userActions.getStores({ stores_list: [{ store_id: storeId }] })
    }
  }, [orgId, storeId])

  useFetchingIncompletedOrders(storeId)

  const handleShowUnifinedMenuMobile = () => {
    const element = document.getElementsByClassName(
      'bs-unified__side-bar-container',
    )[0]
    element?.classList.add('bs-unified__side-bar-container-open')
    element?.setAttribute('style', 'z-index: 998')
  }

  return (
    <Guard accessWhen={isValidUser} fallbackComp={<Navigate to="/login" />}>
      <AnnouncementProvider
        apiUrl={REACT_APP_COM_HUB_API_URL}
        orgId={orgId}
        enabled={!!orgId && isCF1997Enabled}
        getFirebaseToken={fbAuth.getToken}
      >
        <NotificationProvider
          config={{
            apiBaseUrl: REACT_APP_NOTIFICATION_URL,
            streamEndpoint: '/notifications/stream',
            notificationEndpoint: '/notifications',
            bsOrgId: orgId,
            auth: {
              getFirebaseToken: fbAuth.getToken,
              sessionTokenEndpoint: '/notifications/stream/token',
            },
            timezone: timezoneNameUserSetting,
            navigate: navigate,
            onNotification: handleNotification,
            soundConfig,
            triggerEvents,
            warehousesConfig,
            isV2: isNotiV2,
          }}
        >
          <Announcement renderGlobalBanner={true} renderPopup={true} />
          <div className={classes.body}>
            <UnifiedNavbar />
            <div className={classes.content}>
              {/* <Topbar /> */}
              {location.pathname?.includes(ADMIN_ROUTES.HOME) ? null : (
                <div className={classes.showBtnUnifined}>
                  <img
                    role="presentation"
                    src="/icons/nav.svg"
                    alt="nav"
                    onClick={handleShowUnifinedMenuMobile}
                  />
                </div>
              )}
              <BackToTopButton showBelow={500} disable={disable} />
              <StoreGuard>
                <RoleGuard>
                  {/* <Suspense fallback={null}> */}
                  <div id="content" className={classes.childrenWrap}>
                    {authState.status === 'SUCCESS' && children}
                  </div>
                  {/* </Suspense> */}
                  {/* {authState.status !== 'SUCCESS' && <DataLoading loading />} */}
                  {/* {children} */}
                </RoleGuard>
              </StoreGuard>
            </div>
          </div>
        </NotificationProvider>
      </AnnouncementProvider>
    </Guard>
  )
}

AdminLayout.propTypes = {
  classes: PropTypes.shape({
    body: PropTypes.string,
    content: PropTypes.string,
    showBtnUnifined: PropTypes.string,
    childrenWrap: PropTypes.string,
  }).isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
}

//@ts-ignore
export default withStyles(style)(AdminLayout)
